import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
} from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
  addCustomer,
  deleteCustomer,
  fetchCustomersList,
  updateCustomer,
} from "../../../slices/thunks";
import CustomerForm from "../../Forms/Master/AddCustomer";
import CustomPagination from "../../../Components/Common/CustomePagination";
import ConfirmModal from "../../../Components/Common/ConfirmModal";
import ViewCustomerModal from "../../../Modals/Customer/ViewCustomerModal";
import EditCustomerForm from "../../Forms/Master/EditCustomer";
import NF from "../../../assets/images/file.png";

const Customers = () => {
  const dispatch = useDispatch();

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceSearch, setDebouncedSearch] = useState("");
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  // const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const selectCustomerListState = (state) => state;

  const customerSelector = createSelector(selectCustomerListState, (state) => ({
    customers: state.Customer.customers,
    loading: state.Customer.loading,
    error: state.Customer.error,
    pagination: state.Customer.pagination,
  }));

  const { customers, loading, error, pagination } =
    useSelector(customerSelector);

  const toggleForm = () => setIsFormVisible(!isFormVisible);
  const toggleConfirmModal = () => setIsConfirmModalOpen(!isConfirmModalOpen);

  const toggleViewModal = () => setIsViewModalOpen(!isViewModalOpen);

  const handleAddCustomer = async (newCustomer) => {
    await dispatch(addCustomer(newCustomer));
    await dispatch(
      fetchCustomersList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
      })
    );
    setIsFormVisible(false);
  };

  const handleDeleteClick = (customerId) => {
    setSelectedCustomerId(customerId);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    await dispatch(deleteCustomer(selectedCustomerId));
    const params = {
      pageNo,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      isPagination: 1,
    };
    await dispatch(fetchCustomersList(params));
  };

  const handleEditCustomer = async (updatedCustomer) => {
    await dispatch(updateCustomer(selectedCustomer.id, updatedCustomer));
    await dispatch(
      fetchCustomersList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
      })
    );
    setIsEditMode(false);
    setIsFormVisible(false);
    setSelectedCustomer(null);
  };

  // const handleEdit = (tax) => {
  //   setSelectedTax(tax);
  //   setIsEditModalOpen(true);
  // };

  // Debounce search input
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  // Fetch customers
  useEffect(() => {
    dispatch(
      fetchCustomersList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
        searchTerm: debounceSearch.trim(),
      })
    );
  }, [dispatch, pageNo, pageSize, debounceSearch]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <h4 className="card-title mb-0">
                        {isFormVisible ? "Add Customer"  : "Customer List"}
                      </h4>
                    </div>

                    <Button
                      color="primary"
                      className="btn btn-sm"
                      onClick={toggleForm}
                    >
                      {isFormVisible ? (
                        <i className="ri-arrow-left-line align-bottom me-1"></i>
                      ) : (
                        <i className="ri-add-line align-bottom me-1"></i>
                      )}
                      {isFormVisible ? "Back to List" : "Add Customer"}
                    </Button>
                  </div>
                </CardHeader>

                <CardBody>
                  {isFormVisible ? (
                    isEditMode ? (
                      <EditCustomerForm
                        customer={selectedCustomer}
                        onSubmit={handleEditCustomer}
                        onCancel={() => {
                          setIsEditMode(false);
                          setIsFormVisible(false);
                          setSelectedCustomer(null);
                        }}
                      />
                    ) : (
                      <CustomerForm onSubmit={handleAddCustomer} />
                    )
                  ) : (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="search-box w-25">
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Search customers..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setPageNo(0);
                            }}
                          />
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table align-middle table-nowrap">
                          <thead className="table-light">
                            <tr>
                              <th>Sr. No</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Mobile</th>
                              <th>Address</th>
                              <th>GST No</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              [...Array(pageSize)].map((_, i) => (
                                <tr key={i}>
                                  {[...Array(8)].map((_, j) => (
                                    <td key={j}>
                                      <Skeleton height={15} width={80} />
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : error ? (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center text-danger"
                                >
                                  {error}
                                </td>
                              </tr>
                            ) : customers?.length === 0 ? (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center text-muted"
                                >
                                  <img
                                    src={NF}
                                    alt="No File"
                                    style={{
                                      width: "500px",
                                      marginTop: "10px",
                                    }}
                                  />
                                </td>
                              </tr>
                            ) : (
                              customers?.map((cust, index) => (
                                <tr
                                  key={cust.id}
                                  onClick={() => {
                                    setSelectedCustomer(cust);
                                    toggleViewModal();
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  <td>{pageNo * pageSize + index + 1}</td>
                                  <td>{cust?.name || "-"}</td>
                                  <td>{cust?.email || "-"}</td>
                                  <td>{cust?.mobileNo || "-"}</td>
                                  <td>{cust?.address || "-"}</td>
                                  <td>{cust?.gstnNo || "-"}</td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-success btn-icon waves-effect waves-light"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedCustomer(cust);
                                          setIsEditMode(true);
                                          setIsFormVisible(true);
                                        }}
                                      >
                                        <i className="ri-edit-2-line"></i>
                                      </button>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger btn-icon waves-effect waves-light"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteClick(cust?.id);
                                        }}
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-3">
                        <CustomPagination
                          currentPage={pagination?.currentPage}
                          totalPages={pagination?.totalPages}
                          onPageChange={(page) => setPageNo(page)}
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          setPageNo={setPageNo}
                        />
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        toggle={toggleConfirmModal}
        title="Delete Costomer"
        message="Are you sure you want to delete this customer?"
        onConfirm={confirmDelete}
      />

      <ViewCustomerModal
        show={isViewModalOpen}
        toggle={toggleViewModal}
        customer={selectedCustomer}
      />
    </React.Fragment>
  );
};

export default Customers;
